import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Tablet, MessageSquare, Package, ShoppingCart,
  Clock, Target, Eye, Headphones, Check,
} from 'lucide-react';
import SectionWrapper from '../../components/marketing/SectionWrapper';
import FeatureCard from '../../components/marketing/FeatureCard';
import ScreenshotShowcase from '../../components/marketing/ScreenshotShowcase';
import CTABanner from '../../components/marketing/CTABanner';
import { ALL_PHOTOS } from '../../components/marketing/photoPool';

export default function KindergartensPage() {
  const { t } = useTranslation('marketing');

  const features = [
    { icon: Tablet, title: t('kindergartens.feature1_title'), description: t('kindergartens.feature1_desc'), color: '#0891b2' },
    { icon: MessageSquare, title: t('kindergartens.feature2_title'), description: t('kindergartens.feature2_desc'), color: '#ea580c' },
    { icon: Package, title: t('kindergartens.feature3_title'), description: t('kindergartens.feature3_desc'), color: '#7c3aed' },
    { icon: ShoppingCart, title: t('kindergartens.feature4_title'), description: t('kindergartens.feature4_desc'), color: '#16a34a' },
  ];

  const benefits = [
    { icon: Clock, text: t('kindergartens.benefit1'), color: '#0891b2' },
    { icon: Target, text: t('kindergartens.benefit2'), color: '#16a34a' },
    { icon: Eye, text: t('kindergartens.benefit3'), color: '#ea580c' },
    { icon: Headphones, text: t('kindergartens.benefit4'), color: '#7c3aed' },
  ];

  return (
    <>
      <section className="pt-24 pb-14 bg-gradient-to-br from-cyan-50/60 via-white to-teal-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700 text-xs font-semibold tracking-wide uppercase mb-6">
              <Tablet className="w-3.5 h-3.5" />
              {t('kindergartens.section_title')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">{t('kindergartens.title')}</h1>
            <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto">{t('kindergartens.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <SectionWrapper background="white">
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Kindergarten tablet attendance"
          direction="right"
          title={t('kindergartens.feature1_title')}
          subtitle={t('kindergartens.feature1_desc')}
          features={[
            { text: t('kindergartens.feature2_title') + ' \u2014 ' + t('kindergartens.feature2_desc') },
            { text: t('kindergartens.feature3_title') + ' \u2014 ' + t('kindergartens.feature3_desc') },
            { text: t('kindergartens.feature4_title') + ' \u2014 ' + t('kindergartens.feature4_desc') },
          ]}
          accentColor="#0891b2"
          placeholderIcon={Tablet}
        />
      </SectionWrapper>

      <SectionWrapper background="gray">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} color={f.color} index={i} />
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper title={t('kindergartens.benefits_title')} background="white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -16 : 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-3 bg-white rounded-xl p-4 ring-1 ring-gray-100 hover:shadow-md transition-shadow"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${b.color}10` }}
              >
                <Check className="w-5 h-5" style={{ color: b.color }} />
              </div>
              <p className="text-gray-700 text-sm font-medium">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      <CTABanner
        title={t('cta.title')}
        subtitle={t('cta.subtitle')}
        buttonText={t('cta.button')}
      />
    </>
  );
}
